import React from 'react';
import { CalendarIcon, ClockIcon } from '@heroicons/react/24/solid';
import {
  ColorName,
  localeNormalizer,
  parseSectionSlotDateTime,
  parseSectionSlotDateTimeSuperShort,
  returnColor,
} from '@youshift/shared/utils';
import { Section, SectionSlot, SupportedLocale } from '@youshift/shared/types';
import { XMarkIcon } from '@heroicons/react/24/outline';

import i18n from '../../utils/i18n';
import { PrefSlotsMapAction } from '../../pages/Manager/IterationConfig/Preferences/utils';

interface SectionSlotChipProps {
  sectionName: string;
  start: string;
  end: string;
  small?: boolean;
  sectionColor?: ColorName;
}

function SectionSlotChip({
  sectionName,
  start,
  end,
  small,
  sectionColor = 'blue',
}: SectionSlotChipProps) {
  const locale = localeNormalizer(i18n.language);
  return (
    <div
      style={{
        backgroundColor: returnColor(sectionColor, 100),
      }}
      className={`flex rounded-full ${small ? 'flex-col px-2 py-1 text-xs' : 'items-center text-sm justify-between gap-2 p-2 mt-2 ml-5 shadow-md'}`}
    >
      {/* Section Name */}
      <div className="flex items-center gap-1">
        <CalendarIcon
          className={`${small ? 'h-3 w-3' : 'h-5 w-5'}`}
          style={{ color: returnColor(sectionColor, 500) }}
        />
        <p style={{ color: returnColor(sectionColor, 800) }}>{sectionName}</p>
      </div>

      {/* Time */}
      <div className="flex items-center gap-1">
        <ClockIcon
          className={`${small ? 'h-3 w-3' : 'h-5 w-5'}`}
          style={{ color: returnColor(sectionColor, 500) }}
        />
        <p style={{ color: returnColor(sectionColor, 800) }}>
          {parseSectionSlotDateTime(start, end, locale, false)}
        </p>
      </div>
    </div>
  );
}

interface CompactSectionSlotChipProps {
  sectionSlot: SectionSlot;
  section: Section;
  locale: SupportedLocale;
  showDay?: boolean;
  handlePrefSlotsMapUpdate?: (
    action: PrefSlotsMapAction,
    isTemporary: boolean,
  ) => void;
  isTemporary?: boolean;
}

export function CompactSectionSlotChip({
  sectionSlot,
  section,
  locale,
  showDay,
  handlePrefSlotsMapUpdate,
  isTemporary = false,
}: CompactSectionSlotChipProps) {
  const { start, end, id_section_slot } = sectionSlot;
  const { name: sectionName, color: sectionColor } = section;
  return (
    <div
      key={sectionSlot.id_section_slot}
      style={{
        backgroundColor: returnColor(sectionColor, 100),
      }}
      className="relative rounded-md whitespace-normal px-2 py-1 text-xs"
    >
      {/* Time */}
      <p style={{ color: returnColor(sectionColor, 800) }}>
        {section.acronym}
        {`: `}
        {parseSectionSlotDateTimeSuperShort(start, end, locale, showDay)}
      </p>
      {handlePrefSlotsMapUpdate && (
        <button
          onClick={() =>
            handlePrefSlotsMapUpdate(
              {
                type: 'ORPHAN_SECTION_SLOT',
                sectionSlotId: id_section_slot,
              },
              isTemporary,
            )
          }
          className="absolute -top-2 -right-1.5"
        >
          <XMarkIcon className="w-4 h-4 text-red-600" />
        </button>
      )}
    </div>
  );
}

export default SectionSlotChip;
